import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const LOGO_TAP_COUNT = 5;          // 5 taps to unlock
const LOGO_TAP_WINDOW_MS = 4000;   // generous 4-second rolling window
const TAPS_STORAGE_KEY = "__ar_logo_taps";  // sessionStorage — survives logo-triggered route remounts

export default function Navbar({ user, onLogout }) {
  const [showAdmin, setShowAdmin] = useState(false);

  const onLogoTap = (e) => {
    // Persist taps in sessionStorage so the counter survives any navigation that
    // happens between clicks (logo is a Link → / → Navbar remounts on cross-page).
    let taps = [];
    try {
      const raw = window.sessionStorage.getItem(TAPS_STORAGE_KEY);
      if (raw) taps = JSON.parse(raw);
    } catch {}
    const now = Date.now();
    taps = (Array.isArray(taps) ? taps : []).filter((t) => now - t < LOGO_TAP_WINDOW_MS);
    taps.push(now);

    if (taps.length >= LOGO_TAP_COUNT) {
      // Easter egg unlocked — open the admin modal, suppress the / navigation
      try { window.sessionStorage.removeItem(TAPS_STORAGE_KEY); } catch {}
      e.preventDefault();
      setShowAdmin(true);
      return;
    }

    try { window.sessionStorage.setItem(TAPS_STORAGE_KEY, JSON.stringify(taps)); } catch {}

    // Once they've tapped twice in quick succession we know they're in
    // "easter-egg mode" — kill the navigation so they can keep tapping in place
    // (otherwise the page load between taps eats the rolling window).
    if (taps.length >= 2) {
      e.preventDefault();
    }
    // First tap → let the Link navigate to / normally.
  };

  return (
    <header className="sticky top-0 z-40 bg-[#0F0D0C]/85 backdrop-blur-md border-b border-[#F5F2EB]/10">
      <div className="mx-auto max-w-7xl px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={onLogoTap}>
          <span className="w-2 h-2 bg-[#D95B38] inline-block" />
          <span className="font-serif italic text-2xl tracking-tight">AgentReply</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] hidden sm:inline">
            / voice agents
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em] text-[#A39A8F]">
          <a href="/#features" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-features">Features</a>
          <a href="/#pricing" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-pricing">Pricing</a>
          <Link to="/services" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-services">For agencies</Link>
          <Link to="/blog" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-blog">Blog</Link>
          <Link to="/about" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-about">About</Link>
          <Link to="/help" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-help">Help</Link>
          <a href="/#faq" className="hover:text-[#F5F2EB] transition-colors" data-testid="nav-faq">FAQ</a>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="font-mono text-xs uppercase tracking-[0.2em] text-[#F5F2EB] border border-[#F5F2EB]/30 px-4 py-2 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
                  data-testid="nav-logout"
                >
                  Logout
                </button>
              )}
            </>
          ) : (
            <Link
              to="/login"
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#F5F2EB] bg-[#D95B38] px-5 py-2.5 btn-press"
              data-testid="nav-login"
            >
              Sign in →
            </Link>
          )}
        </div>
      </div>

      {showAdmin && <AdminLoginModal onClose={() => setShowAdmin(false)} />}
    </header>
  );
}

function AdminLoginModal({ onClose }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Detect existing session — if user is already an admin, single button takes them to ops.
  useEffect(() => {
    let mounted = true;
    api.get("/auth/me")
      .then((r) => { if (mounted) setCurrentUser(r.data || null); })
      .catch(() => { if (mounted) setCurrentUser(null); })
      .finally(() => { if (mounted) setCheckingSession(false); });
    return () => { mounted = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email: email.trim(), password });
      if (!r.data?.user?.is_admin) {
        setError("This account is not an admin. Use the regular sign-in page.");
        setLoading(false);
        return;
      }
      onClose();
      navigate("/dashboard/admin/health");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
      setLoading(false);
    }
  };

  const startGoogleAdminLogin = () => {
    try { window.localStorage.setItem("post_auth_redirect", "/dashboard/admin/health"); } catch {}
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const goToOpsDirectly = () => {
    onClose();
    navigate("/dashboard/admin/health");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="admin-login-modal"
    >
      <div
        className="relative w-full max-w-md bg-[#1A1715] border border-[#D95B38]/30 p-6 sm:p-8 my-4 sm:my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="admin-login-close"
          className="absolute top-4 right-4 text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={1.6} />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3" /> Admin access
        </div>

        {checkingSession ? (
          <div className="font-mono text-xs text-[#A39A8F] flex items-center gap-2 py-6">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking session…
          </div>
        ) : currentUser?.is_admin ? (
          // Path 1: already signed in AND admin → one-click shortcut
          <>
            <h3 className="font-serif text-2xl text-[#F5F2EB] mb-2 leading-tight">Welcome back, {currentUser.name?.split(" ")[0] || "admin"}.</h3>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              Signed in as <span className="text-[#E0D8CA]">{currentUser.email}</span>. Drop straight into ops.
            </p>
            <button
              type="button"
              onClick={goToOpsDirectly}
              data-testid="admin-login-go-ops"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] px-6 py-3 transition-colors btn-press flex items-center justify-center gap-2"
            >
              Enter ops dashboard <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <h3 className="font-serif text-2xl text-[#F5F2EB] mb-2 leading-tight">Authenticate</h3>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              Sign in with the account that has admin access. You'll land straight on the ops dashboard.
            </p>

            {/* Google — works for Google-OAuth admins (no password). */}
            <button
              type="button"
              onClick={startGoogleAdminLogin}
              data-testid="admin-login-google"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] px-6 py-3 transition-colors flex items-center justify-center gap-3 mb-5"
            >
              <svg width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32zM12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23zM5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84zM12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 border-t border-[#F5F2EB]/10" />
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#7A7068]">or with password</span>
              <div className="flex-1 border-t border-[#F5F2EB]/10" />
            </div>

            <form onSubmit={submit} className="space-y-5">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Email</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
                />
              </label>

              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Password</span>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-login-password"
                    className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 pr-10 focus:outline-none focus:border-[#D95B38]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    data-testid="admin-login-password-toggle"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
                  </button>
                </div>
              </label>

              {error && (
                <div className="font-mono text-xs text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2" data-testid="admin-login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] disabled:opacity-60 px-6 py-3 transition-colors btn-press flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Authenticating…" : "Enter ops dashboard →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
